import { RefreshOutcome } from '@metadevprofile/shared';
import type { ApiErrorShape, ApiRequestDescriptor, SharedBaseQueryFn } from '@metadevprofile/shared/api';
import { getCurrentTokenGeneration, httpClient, refreshCoordinator } from '@/common/lib/http-client';

type EdenRequestOptions = { query?: Record<string, unknown> };

type EdenErrorValue = { message?: unknown; code?: unknown } | string | undefined;

type EdenResult = {
  data: unknown;
  error: { status: number; value?: EdenErrorValue } | null;
  status: number;
};

/**
 * The Eden Treaty client (`httpClient`) is a runtime Proxy: every property/param access returns
 * another proxy node, and calling the final node issues the request (GET reads `query` off its
 * single argument, every other method takes `(body, { query })`). TypeScript can't express
 * "indexable AND callable with either a route-param object or a request" for an arbitrary,
 * descriptor-driven path ahead of time — this local shape is the one deliberate escape hatch for
 * walking `ApiRequestDescriptor.path` generically. Every concrete RTK Query endpoint still gets
 * full typing from its own `builder.query<Response, Arg>` generics; only this bridge is untyped.
 */
type EdenProxyNode = {
  [key: string]: EdenProxyNode;
} & ((arg?: unknown, options?: EdenRequestOptions) => EdenProxyNode | Promise<EdenResult>);

const walkPath = (root: EdenProxyNode, path: ApiRequestDescriptor['path']): EdenProxyNode => {
  let node = root;
  for (const segment of path) {
    node = typeof segment === 'string' ? node[segment] : (node({ [segment.param]: segment.value }) as EdenProxyNode);
  }
  return node;
};

const invoke = (node: EdenProxyNode, descriptor: ApiRequestDescriptor): Promise<EdenResult> => {
  const method = node[descriptor.method];
  const options: EdenRequestOptions | undefined = descriptor.query ? { query: descriptor.query } : undefined;
  // GET reads its query from the sole argument; every other verb takes `(body, options)` — see
  // the treaty proxy's `apply` trap this bridge is walking.
  const result = descriptor.method === 'get' ? method(options) : method(descriptor.body, options);
  return result as Promise<EdenResult>;
};

const normalizeError = (result: EdenResult): ApiErrorShape => {
  const status = result.error?.status ?? result.status;
  const value = result.error?.value;

  if (typeof value === 'string') return { status, message: value };
  if (value && typeof value === 'object') {
    const message = typeof value.message === 'string' ? value.message : undefined;
    const code = typeof value.code === 'string' ? value.code : undefined;
    return { status, message, code };
  }
  return { status };
};

type RequestOutcome = { ok: true; result: EdenResult } | { ok: false; error: ApiErrorShape };

const runRequest = async (node: EdenProxyNode, descriptor: ApiRequestDescriptor): Promise<RequestOutcome> => {
  try {
    return { ok: true, result: await invoke(node, descriptor) };
  } catch (thrown) {
    // treaty's fetch wrapper only rejects on a genuine network failure (offline, DNS, CORS) —
    // HTTP error statuses resolve normally with `result.error` set instead of throwing.
    return { ok: false, error: { status: 0, message: thrown instanceof Error ? thrown.message : 'Network error' } };
  }
};

/**
 * `BaseQueryFn` registered with `setBaseQueryImpl` at store boot. Reuses `httpClient` as-is for
 * the proactive Bearer injection; here it only handles the reactive path — a 401 that slipped
 * past the proactive refresh triggers `refreshCoordinator` (shared with `httpClient`'s own
 * proactive flow, so both paths dedup through the same single-flight + cross-tab lock) and
 * retries the request once. Never notifies — surfacing errors to the user is a later middleware.
 */
export const edenBaseQuery: SharedBaseQueryFn = async (descriptor) => {
  const node = walkPath(httpClient as unknown as EdenProxyNode, descriptor.path);
  const requestGeneration = getCurrentTokenGeneration();

  const first = await runRequest(node, descriptor);
  if (!first.ok) return { error: first.error };
  if (!first.result.error) return { data: first.result.data };

  if (first.result.status === 401) {
    const outcome = await refreshCoordinator(requestGeneration);

    if (outcome === RefreshOutcome.REFRESHED) {
      const retry = await runRequest(node, descriptor);
      if (!retry.ok) return { error: retry.error };
      return retry.result.error ? { error: normalizeError(retry.result) } : { data: retry.result.data };
    }
    // INVALID: refreshCoordinator's performRefresh already purged tokens, reset the feature
    // stores, and redirected to sign-in. UNAVAILABLE: surface the original error untouched.
  }

  return { error: normalizeError(first.result) };
};
